import { FormEvent, useState } from 'react';
import cn from 'classnames';
import { UserReviewChanges } from 'api';
import { UserReview } from 'types';
import { Button, Select, SelectOption } from 'components';
import { SimpleRating } from '../../components/user-review-form/simple-rating';
import styles from './moderation.module.scss';

const NAME_MIN = 2;
const NAME_MAX = 255;
const TEXT_MIN = 50;
const TEXT_MAX = 10000;
const NOTE_MAX = 500;

type ReviewEditorProps = {
  review: UserReview;
  bookOptions: SelectOption[];
  isSaving: boolean;
  saveFailed: boolean;
  onSave: (changes: UserReviewChanges) => void;
  onCancel: () => void;
};

export const ReviewEditor = ({
  review,
  bookOptions,
  isSaving,
  saveFailed,
  onSave,
  onCancel,
}: ReviewEditorProps) => {
  const [bookId, setBookId] = useState(review.bookId);
  const [author, setAuthor] = useState(review.authorName);
  const [rating, setRating] = useState<number | null>(review.rating);
  const [language, setLanguage] = useState(review.language);
  const [text, setText] = useState(review.text);
  const [note, setNote] = useState(review.moderationNote ?? '');

  const name = author.trim();
  const body = text.trim();
  const errors = {
    author: name.length < NAME_MIN ? `At least ${NAME_MIN} characters.` : '',
    text: body.length < TEXT_MIN ? `At least ${TEXT_MIN} characters.` : '',
    language: language.trim() ? '' : 'Required.',
  };
  const isValid =
    !errors.author && !errors.text && !errors.language && rating !== null;

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();

    if (isValid && !isSaving) {
      onSave({
        author: name,
        text: body,
        rating: rating ?? review.rating,
        bookId,
        language: language.trim(),
        moderationNote: note.trim() || null,
      });
    }
  };

  return (
    <form className={styles.editor} onSubmit={handleSubmit} noValidate>
      <div className={styles.editorRow}>
        <div className={cn(styles.editorField, styles.editorWide)}>
          <span className={styles.editorLabel}>Book</span>
          <Select
            options={bookOptions}
            value={bookId}
            onChange={(value) => setBookId(Number(value) || review.bookId)}
          />
        </div>
        <label className={styles.editorField}>
          <span className={styles.editorLabel}>Reviewer</span>
          <input
            className={cn(styles.editorInput, {
              [styles.editorInvalid]: errors.author,
            })}
            value={author}
            maxLength={NAME_MAX}
            onChange={(e) => setAuthor(e.target.value)}
          />
          {errors.author && (
            <span className={styles.error}>{errors.author}</span>
          )}
        </label>
        <label className={cn(styles.editorField, styles.editorNarrow)}>
          <span className={styles.editorLabel}>Language</span>
          <input
            className={cn(styles.editorInput, {
              [styles.editorInvalid]: errors.language,
            })}
            value={language}
            maxLength={10}
            onChange={(e) => setLanguage(e.target.value)}
          />
        </label>
      </div>

      <div className={styles.editorField}>
        <span className={styles.editorLabel}>Rating</span>
        <SimpleRating value={rating} onChange={setRating} />
      </div>

      <label className={styles.editorField}>
        <span className={styles.editorLabel}>Review</span>
        <textarea
          className={cn(styles.editorInput, styles.editorTextarea, {
            [styles.editorInvalid]: errors.text,
          })}
          value={text}
          maxLength={TEXT_MAX}
          rows={6}
          onChange={(e) => setText(e.target.value)}
        />
        <span className={errors.text ? styles.error : styles.editorHint}>
          {errors.text || `${body.length} / ${TEXT_MAX}`}
        </span>
      </label>

      <label className={styles.editorField}>
        <span className={styles.editorLabel}>Moderation note</span>
        <input
          className={styles.editorInput}
          value={note}
          maxLength={NOTE_MAX}
          placeholder="Only visible to admins"
          onChange={(e) => setNote(e.target.value)}
        />
      </label>

      <div className={styles.actions}>
        {saveFailed && <span className={styles.error}>Could not save</span>}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={isSaving}
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="secondary"
          size="sm"
          loading={isSaving}
          disabled={!isValid}
        >
          Save
        </Button>
      </div>
    </form>
  );
};
